import { useParams } from "react-router-dom";
import { PulseCard } from "../components/PulseCard";
import { CrewSinceLastSeenSummary } from "../components/SinceLastSeenSummary";
import type { RunProjection } from "../domain";
import { formatAge } from "../lib/time";
import { useRemote } from "../state/RemoteContext";

export function presentRunProgress(
  run: Pick<RunProjection, "state" | "progress" | "nextCheckpoint" | "plan">,
) {
  const nextCheckpoint = run.nextCheckpoint.trim();
  const hasProgressContext = run.plan.length > 0 || nextCheckpoint.length > 0;
  return {
    eyebrow: hasProgressContext ? `${run.state} · ${run.progress}%` : run.state,
    meta: nextCheckpoint.length > 0 ? `Next · ${nextCheckpoint}` : undefined,
    progress: hasProgressContext ? run.progress : undefined,
  };
}

export function hasRunPlan(run: Pick<RunProjection, "plan">) {
  return run.plan.length > 0;
}

export function CrewPulseScreen() {
  const { crewId } = useParams();
  const { fixture, setDialog, can, canMutate, mutationBlockReason, actions } = useRemote();
  const crew = fixture.crews.find((candidate) => candidate.id === crewId);
  if (!crew) return <div className="screen empty-state"><h1>Crew unavailable</h1><p>This Crew is not in the current remote scope.</p></div>;

  const decision = crew.decisions.find((item) => item.state === "waiting");
  return (
    <div className="screen">
      <header className="screen-heading crew-screen-heading">
        <CrewSinceLastSeenSummary
          since={crew.since}
          fromLabel={new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(crew.since.from))}
        />
        <h1>Crew Pulse</h1>
      </header>

      <section className="pulse-list" aria-label="Crew Pulse">
        {decision && (
          <PulseCard kind="decision" eyebrow="Needs you · decision" title={decision.title} summary={decision.consequence} meta={`${decision.requestedBy} · ${formatAge(decision.requestedAt)} · ${decision.id}`} operationState={actions.operationsByTarget[decision.id]?.state}>
            <button type="button" className="attention-button" onClick={() => setDialog({ type: "decision", crewId: crew.id, decisionId: decision.id })}>Review</button>
          </PulseCard>
        )}
        {crew.runs.map((run) => {
          const runAction = run.state === "paused" ? "resume" : "pause";
          const runCapability = runAction === "resume" ? "run.resume" : "run.pause";
          const controllable = run.allowedActions.includes(runAction);
          const allowed = controllable && can(runCapability) && canMutate;
          const visualKind = run.state === "completed" ? "completed" : run.state === "failed" ? "fault" : run.state === "running" ? "running" : "neutral";
          const presentation = presentRunProgress(run);
          return (
            <PulseCard key={run.id} kind={visualKind} eyebrow={presentation.eyebrow} title={run.title} meta={presentation.meta} progress={presentation.progress} operationState={actions.operationsByTarget[run.id]?.state}>
              {controllable && (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={!allowed}
                  title={allowed ? undefined : can(runCapability) ? mutationBlockReason ?? undefined : `${runCapability} is not granted to this device`}
                  onClick={() => setDialog({ type: "run", crewId: crew.id, runId: run.id, action: runAction })}
                >
                  {run.state === "paused" ? "Resume" : "Pause"}
                </button>
              )}
              {run.state === "completed" && can("evidence.read") && crew.evidence.length > 0 && <button type="button" className="secondary-button" onClick={() => setDialog({ type: "evidence", crewId: crew.id, evidenceIds: crew.evidence.map((item) => item.id) })}>Evidence</button>}
            </PulseCard>
          );
        })}
      </section>

      {crew.runs.filter(hasRunPlan).map((run) => (
        <details className="run-plan" key={`${run.id}-plan`}>
          <summary><span className="plan-ring" aria-hidden="true" />Run plan · {run.plan.filter((step) => step.state === "done").length}/{run.plan.length}</summary>
          <ol>
            {run.plan.map((step) => <li key={step.id} data-state={step.state}><span aria-hidden="true">{step.state === "done" ? "✓" : ""}</span>{step.title}{run.state === "paused" && step.state === "current" ? " · paused" : ""}</li>)}
          </ol>
        </details>
      ))}

      <section className="member-section" aria-labelledby="member-heading">
        <h2 id="member-heading" className="section-title">Crew · {crew.members.length} members</h2>
        {crew.members.map((member) => (
          <div className="member-row" key={member.id}>
            <span className="member-health" data-health={member.health} aria-hidden="true" />
            <strong>{member.name}</strong>
            <span>{member.currentWork}</span>
            <small>{formatAge(member.lastBeatAt)}</small>
          </div>
        ))}
      </section>

      {(crew.graphs?.length ?? 0) > 0 && (
        <section className="graph-section" aria-labelledby="graph-heading">
          <h2 id="graph-heading" className="section-title">Graphs &amp; resources · {crew.graphs?.length}</h2>
          <div className="graph-list">
            {crew.graphs?.map((graph) => {
              const system = crew.systems?.find((candidate) => candidate.id === graph.systemId);
              const placements = crew.memberGraphPlacements?.filter((candidate) => candidate.graphId === graph.id) ?? [];
              return (
                <article className="graph-row" key={graph.id}>
                  <span className="health-dot" data-health={graph.state} aria-hidden="true" />
                  <div><strong>{graph.label}</strong><small>{graph.graphType} · {graph.state}{system ? ` · ${system.label}` : ""}</small></div>
                  <span>{graph.resourceIds.length} resources · {placements.length} members</span>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
